import { Box, SelectChangeEvent } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import BriaDropDown, { BriaDropDownProps, DropDownItem } from "../../../../components/common/BriaDropDown/BriaDropDown";
import RouterConstants from "../../../../constants/RouterConstants";
import { useAppStore } from "../../../../hooks/useStores";
import iframeStore from "../../../IframeNew/iframe-store";
import { TgModel } from "../store/tg-model-store";
import styles from "./NewTgDropdown.module.scss";
import useTgDropdown from "./useTgDropdown";

type Props = Omit<BriaDropDownProps<string>, "onChange" | "items"> & {
	onChange: (e: SelectChangeEvent<string>, item: DropDownItem<string, TgModel>) => void;
	showCheckpoints?: boolean;
	alwaysRefresh?: boolean;
	disabledItems?: (item: DropDownItem<string, TgModel>) => boolean | { disabled: boolean; errorMessage?: string };
	hideTrainButton?: boolean;
};

const NewTgDropdown = ({
	value,
	onChange,
	showCheckpoints = false,
	loading,
	alwaysRefresh = false,
	disabledItems,
	hideTrainButton = false,
	...rest
}: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "app.image.config.models" });
	const { t: tModels } = useTranslation("tg", { keyPrefix: "models" });

	const { tgStore } = useAppStore();
	const { tgModelStore, tgProjectStore } = tgStore;

	const location = useLocation();
	const isEvaluation = location.pathname.includes(RouterConstants.NEW_TG.MODELS.EVALUATION.path);

	const { projectId } = useParams();
	const { createDropdownItems, loadingCreateItems } = useTgDropdown();
	const [localItems, setLocalItems] = useState<DropDownItem<string, TgModel>[]>([]);
	const [searchValue, setSearchValue] = useState("");
	const filteredItems = localItems.filter(
		(item) =>
			item.key.toLowerCase().includes(searchValue.toLowerCase()) ||
			item.extraData?.description.toLowerCase().includes(searchValue.toLowerCase()) ||
			item.value.toString().includes(searchValue),
	);

	const itemsWithSubItems: DropDownItem<string, TgModel>[] = filteredItems.map((item) => {
		const disabledResult = disabledItems ? disabledItems(item) : false;
		const isDisabled = typeof disabledResult === "boolean" ? disabledResult : disabledResult.disabled;
		const errorMessage = typeof disabledResult === "boolean" ? undefined : disabledResult.errorMessage;

		return {
			...item,
			customSelectedKey: (
				<Box key={item.extraData?.id} className={styles.TGItem}>
					<Box className={styles.itemWrap}>
						<Box className={styles.itemText}>{item.extraData?.name}</Box>
						<Box className={styles.badgeContainer}>
							<Box className={clsx(styles.badgeClassName, styles.badge)}>
								{item.extraData?.training_version}
							</Box>
							{isEvaluation && item.extraData?.active_model_version !== "default" && (
								<Box className={clsx(styles.badgeClassName, styles.badge)}>
									{item.extraData?.active_model_version}
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			),
			disabled: isDisabled,
			tooltip: isDisabled ? errorMessage || tModels("disabledTooltip") : undefined,
			subItems:
				(showCheckpoints &&
					item.extraData?.checkpoint_steps?.map((step) => ({
						value: step,
						key: step,
						label: step,
						disabled: isDisabled,
					}))) ||
				[],
			defaultSubItemValue:
				showCheckpoints && item.extraData?.active_model_version !== "default"
					? item.extraData?.active_model_version
					: undefined,
		};
	});

	useEffect(() => {
		(async () => {
			if (!loadingCreateItems) {
				setLocalItems(await createDropdownItems(alwaysRefresh, projectId));
			}
		})();
	}, [tgModelStore.modelsList, tgProjectStore.paginatedModels.items]);

	const handleChange = (e: SelectChangeEvent<string>, child?: any) => {
		if (child && (child as any).label) {
			const checkpoint = String(child.value);
			const parent = filteredItems.find((it) => it.value === value) || undefined;
			const forwarded: DropDownItem<string, TgModel> = {
				...(parent || ({} as any)),
				value: String(value),
				key: parent?.key || String(value),
				extraData: parent?.extraData ? { ...parent.extraData, active_model_version: checkpoint } : undefined,
			};

			setLocalItems(
				localItems.map((item) => (item.value === value ? { ...item, extraData: forwarded.extraData } : item)),
			);
			onChange?.(e, forwarded);
			return;
		}
		const selected = filteredItems.find((it) => it.value === e.target.value);
		if (selected) {
			onChange?.(e, selected as any);
		} else {
			onChange?.(e, { value: "", key: "", label: "", extraData: undefined } as any);
		}
	};

	const handleTrainButtonClick = (e: any) => {
		e.preventDefault();
		e.stopPropagation();
		window.open(RouterConstants.NEW_TG.PROJECTS.fullPath);
	};

	return (
		<BriaDropDown
			placeholder={t("placeholder")}
			value={value}
			onChange={handleChange}
			searchValue={searchValue}
			onSearchChange={(e) => setSearchValue(e.target.value)}
			loading={loadingCreateItems || loading}
			searchable
			debounceSearch
			searchIcon
			SearchPlaceholder={t("searchPlaceholder")}
			menuMaxHeight="70%"
			menuMaxWidth="0px"
			endStickyItem={
				!iframeStore.isIframe() &&
				!hideTrainButton && (
					<Box onClick={handleTrainButtonClick} className={styles.trainModelBtn}>
						{t("trainModelBtn")}
					</Box>
				)
			}
			{...rest}
			items={itemsWithSubItems}
		/>
	);
};

export default observer(NewTgDropdown);
